import {
  convertBundle,
  legacyBundles,
} from '@contentlab/views/contentlab/pricing/utils';
import {ContentLabSubscription} from '@/types/models/pricing/contentLabSubscription';

/**
 * Get the active Content Lab subscription for the Content Studio product.
 * It filters the subscriptions to find the one with the 'content-studio' product
 * and returns the one with priority given to new bundles over legacy ones.
 * For bundles of the same type (new vs legacy), it returns the one with the lowest tier.
 *
 * @param {ContentLabSubscription[] | undefined} subscriptions - The list of subscriptions.
 * @returns {ContentLabSubscription | undefined} The active subscription or undefined if not found.
 */
function getActiveSubscription(
  subscriptions: ContentLabSubscription[] | undefined
): ContentLabSubscription | undefined {
  const isLegacyBundle = (bundle: string): boolean => {
    const bundleEnum = convertBundle(bundle);
    return bundleEnum ? legacyBundles.includes(bundleEnum) : false;
  };
  return subscriptions
    ? subscriptions
        .filter(
          (s: ContentLabSubscription) =>
            s.plan?.quoteDetails?.product === 'content-studio'
        )
        .sort((a: ContentLabSubscription, b: ContentLabSubscription) => {
          const aBundleIsLegacy = isLegacyBundle(a.plan.quoteDetails.bundle);
          const bBundleIsLegacy = isLegacyBundle(b.plan.quoteDetails.bundle);

          // If one is legacy bundle and other is new, prioritize new bundle
          if (aBundleIsLegacy && !bBundleIsLegacy) return 1;
          if (!aBundleIsLegacy && bBundleIsLegacy) return -1;

          // If both are same type (both legacy or both new), sort by tier
          return a.plan.tier - b.plan.tier;
        })[0]
    : undefined;
}

export default getActiveSubscription;
